/**
 * Mock data for the Nimbus × Solomon YaaS **Program Policies** page
 * (client console).
 *
 * Persona: **Nimbus**, a consumer crypto neobank / DeFi savings app. These
 * are the policies Nimbus creates for its incentive **Programs** (Welcome
 * Bonus, Savings Tier Boost, Direct Deposit Boost, Referral Rewards,
 * Loyalty Holding Rewards) — distinct from policies the issuer applies to
 * the client's positions, which are read-only and live with Positions.
 *
 * Per-program model: a **policy type** (e.g. "Minimum Balance") can have
 * many **instances**, each a configured value attached to the program(s)
 * that use it. Same type + different value = two instances. The type id
 * matches a program catalog id; Active = attached to an active Program.
 *
 * Numeric policy values are unitless. The program-id slugs referenced below
 * are the stable ids from `programMocks` (kept for cross-domain links).
 */

import { programsByIds } from "../shared/programMocks";

/** Deterministic mock base58-ish wallet addresses for the locked
 *  Whitelist/Blacklist demo (enough to exercise pagination). */
function mockWalletAddresses(n: number): string[] {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz123456789";
    const out: string[] = [];
    for (let i = 0; i < n; i++) {
        let s = "";
        let seed = ((i + 1) * 2654435761) >>> 0;
        for (let j = 0; j < 44; j++) {
            seed = (seed * 1103515245 + 12345) >>> 0;
            s += chars[seed % chars.length];
        }
        out.push(s);
    }
    return out;
}

/**
 * Policy categories. Folds Compliance into Eligibility, adds One-Time,
 * Tranche, and Distribution Schedule.
 */
export type PolicyCategory = "eligibility" | "allocation" | "one-time" | "tranche" | "distribution-schedule";

export const CATEGORY_LABELS: Record<PolicyCategory, string> = {
    eligibility: "Eligibility",
    allocation: "Allocation",
    "one-time": "One-Time",
    tranche: "Tranche",
    "distribution-schedule": "Distribution Schedule"
};

export const CATEGORY_ORDER: PolicyCategory[] = [
    "eligibility",
    "allocation",
    "one-time",
    "tranche",
    "distribution-schedule"
];

/**
 * The issuer's constraint for a policy type — the bound a client value
 * must satisfy. `range` = a numeric value within [min, max]; `options` =
 * one of a fixed set. This is what client value-creation validates against.
 */
export type PolicyConstraint =
    | { kind: "range"; min: number; max: number; unit: string }
    | { kind: "options"; options: string[] };

/**
 * The issuer's permission on a policy type — how much the client may do
 * with the value. `locked` = issuer-fixed, read-only; `constrained` =
 * editable within the constraint; `delegated` = client sets it freely.
 */
export type PolicyPermission = "locked" | "constrained" | "delegated";

export const PERMISSION_LABELS: Record<PolicyPermission, string> = {
    locked: "Locked",
    constrained: "Constrained",
    delegated: "Delegated"
};

/** A reusable policy *type* — the Category + Type axis + issuer
 *  constraint + permission. The `typeId` matches a program catalog id. */
interface PolicyType {
    typeId: string;
    label: string;
    category: PolicyCategory;
    detail: string;
    /** Issuer's allowed bound — client-edited values are validated here. */
    constraint: PolicyConstraint;
    /** How much the client may change the value. */
    permission: PolicyPermission;
}

const POLICY_TYPES: PolicyType[] = [
    {
        typeId: "min-balance",
        label: "Minimum Balance",
        category: "eligibility",
        detail: "Require a minimum USDv balance for a user to qualify for the program's rewards.",
        constraint: {
            kind: "range",
            min: 100,
            max: Number.POSITIVE_INFINITY,
            unit: "USDv"
        },
        permission: "constrained"
    },
    {
        typeId: "min-holding-period",
        label: "Holding Period",
        category: "eligibility",
        detail: "Minimum number of days a user must hold the balance before rewards qualify.",
        constraint: { kind: "range", min: 0, max: 365, unit: "days" },
        permission: "constrained"
    },
    {
        typeId: "min-distribution-threshold",
        label: "Minimum Distribution Threshold",
        category: "allocation",
        detail: "Minimum USDv amount required before a reward distribution is sent.",
        constraint: { kind: "range", min: 1, max: 10000, unit: "USDv" },
        permission: "constrained"
    },
    {
        typeId: "minimum-threshold",
        label: "Minimum Balance",
        category: "eligibility",
        detail: "Require a minimum balance for a user to qualify for the program's rewards.",
        constraint: {
            kind: "range",
            min: 100,
            max: Number.POSITIVE_INFINITY,
            unit: ""
        },
        permission: "constrained"
    },
    {
        typeId: "balance-amount-tranche",
        label: "Balance Amount Tranche",
        category: "tranche",
        detail: "Reward-multiplier tranches keyed to the balance a user holds.",
        constraint: { kind: "range", min: 1, max: 3, unit: "multiplier" },
        permission: "constrained"
    },
    {
        typeId: "time-held-tranche",
        label: "Time Held Tranche",
        category: "tranche",
        detail: "Reward-multiplier tranches keyed to how long the balance is held.",
        constraint: { kind: "range", min: 1, max: 2, unit: "multiplier" },
        permission: "delegated"
    },
    {
        typeId: "kyc-tier",
        label: "Minimum KYC Tier",
        category: "eligibility",
        detail: "Require users to reach a minimum KYC verification tier to qualify.",
        constraint: { kind: "range", min: 2, max: 3, unit: "tier" },
        permission: "constrained"
    },
    {
        typeId: "holding-period",
        label: "Holding Period",
        category: "distribution-schedule",
        detail: "Minimum number of days a user must hold the balance before rewards vest.",
        constraint: { kind: "range", min: 0, max: 90, unit: "days" },
        permission: "constrained"
    },
    {
        typeId: "fee-split",
        label: "Fee Split",
        category: "allocation",
        detail: "Share of the reward pool the company recipient retains versus distributes to users.",
        constraint: { kind: "range", min: 10, max: 100, unit: "%" },
        permission: "constrained"
    },
    {
        typeId: "wallet-list",
        label: "Specific Access Control",
        category: "eligibility",
        detail: "Restrict eligibility to (or exclude) a list of wallet addresses.",
        constraint: { kind: "options", options: ["Whitelist", "Blacklist"] },
        permission: "locked"
    }
];

/** A configured *instance* of a policy type — its value + the programs it
 *  is attached to. Same type with two values = two instances. */
interface PolicyInstance {
    id: string;
    typeId: string;
    /** The configured value, as a display string (e.g. "1,000"). */
    value: string;
    /** Programs this instance is attached to (`programMocks` ids). */
    programIds: string[];
    /** Per-instance permission override (defaults to the type's). */
    permission?: PolicyPermission;
    /** Wallet addresses for a Whitelist / Blacklist policy. */
    wallets?: string[];
}

// Program-id slugs (stable, from programMocks) mapped to the Nimbus world:
//   bolivia-agent-wallet-activation-drive  → Savings Tier Boost   (active)
//   us-usdpt-settlement-rebate             → Direct Deposit Boost (active)
//   us-revolut-partnership-program         → Loyalty Holding Rewards (active)
//   bolivia-q1-remittance-rewards          → Referral Rewards     (ended)
//   agent-usdpt-settlement-accelerator     → Welcome Bonus        (draft)
const POLICY_INSTANCES: PolicyInstance[] = [
    // Minimum Balance — two instances: different qualifying balance per
    // program. Savings Tier Boost + Direct Deposit Boost require 1,000;
    // Loyalty Holding Rewards lets users in at 100.
    {
        id: "minimum-balance-1k",
        typeId: "minimum-threshold",
        value: "1,000",
        programIds: ["bolivia-agent-wallet-activation-drive", "us-usdpt-settlement-rebate"]
    },
    {
        id: "minimum-balance-100",
        typeId: "minimum-threshold",
        value: "100",
        programIds: ["us-revolut-partnership-program"]
    },
    // Balance-tier multiplier — bigger USDv balances earn a higher boost.
    {
        id: "balance-amount-tranche-default",
        typeId: "balance-amount-tranche",
        value: "3 tranches",
        programIds: [
            "bolivia-agent-wallet-activation-drive",
            "us-usdpt-settlement-rebate",
            "us-revolut-partnership-program"
        ]
    },
    // Time-held multiplier — the longer a user holds, the higher the boost.
    {
        id: "time-held-tranche-default",
        typeId: "time-held-tranche",
        value: "3 tranches",
        programIds: [
            "bolivia-agent-wallet-activation-drive",
            "us-usdpt-settlement-rebate",
            "us-revolut-partnership-program"
        ]
    },
    // KYC gate — users must reach Tier 2 verification to earn rewards.
    {
        id: "kyc-tier-default",
        typeId: "kyc-tier",
        value: "Tier 2",
        programIds: ["bolivia-agent-wallet-activation-drive", "us-usdpt-settlement-rebate"]
    },
    // Holding period — balance must vest before rewards are released.
    {
        id: "holding-period-default",
        typeId: "holding-period",
        value: "30 days",
        programIds: ["us-usdpt-settlement-rebate", "us-revolut-partnership-program"]
    },
    // Fee split — share of the reward pool Nimbus retains vs. passes through.
    {
        id: "fee-split-default",
        typeId: "fee-split",
        value: "20%",
        programIds: ["bolivia-agent-wallet-activation-drive"]
    },
    {
        // Unattached → renders as "Inactive" + no programs. A standing
        // sanctions/abuse exclusion list Nimbus keeps ready to attach.
        id: "wallet-list-default",
        typeId: "wallet-list",
        value: "Blacklist 30 wallets",
        programIds: [],
        wallets: [
            "7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU",
            "DRpbCBMxVnDK7maPM5tGv6MvB3v1sRMC86PZ8okm21hy",
            "9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM",
            ...mockWalletAddresses(27)
        ]
    },
    {
        // Delegated counterpart — the client picks the list type and
        // manages the wallets themselves (no issuer lock).
        id: "wallet-list-delegated",
        typeId: "wallet-list",
        value: "Whitelist 2 wallets",
        programIds: [],
        permission: "delegated",
        wallets: ["5Q544fKrFoe6tsEbD7S8EmxGTJYAKtTVhAW5Q5pge4j1", "FZ7Bf1pBv8YkUL2Y6KkH7T8ckMNFa8wRsLyGREbcdef2"]
    }
];

/** A policy instance enriched with its type's label/category/detail —
 *  one row in the library. */
export interface PolicyDefinition {
    /** Unique instance id. */
    id: string;
    /** Shared policy-type id. */
    typeId: string;
    /** Type label — the row headline (e.g. "Minimum Balance"). */
    type: string;
    /** Category this policy belongs to. */
    category: PolicyCategory;
    /** Configured value for this instance (e.g. "1,000"). */
    value: string;
    /** Longer rule copy shown in the detail view. */
    detail: string;
    /** Programs this instance is attached to. */
    programIds: string[];
    /** Issuer constraint — the bound the editor validates against. */
    constraint: PolicyConstraint;
    /** Issuer permission — gates whether/how the value is editable. */
    permission: PolicyPermission;
    /** Wallet addresses for a Whitelist / Blacklist policy. */
    wallets?: string[];
}

const FALLBACK_CONSTRAINT: PolicyConstraint = {
    kind: "range",
    min: 0,
    max: 0,
    unit: ""
};

export const POLICY_LIBRARY: PolicyDefinition[] = POLICY_INSTANCES.map((inst) => {
    const type = POLICY_TYPES.find((t) => t.typeId === inst.typeId);
    return {
        id: inst.id,
        typeId: inst.typeId,
        type: type?.label ?? inst.typeId,
        category: type?.category ?? "eligibility",
        value: inst.value,
        detail: type?.detail ?? "",
        programIds: inst.programIds,
        constraint: type?.constraint ?? FALLBACK_CONSTRAINT,
        permission: inst.permission ?? type?.permission ?? "constrained",
        wallets: inst.wallets
    };
});

/**
 * True if any program this instance is attached to is **active**. Drives
 * the Active / Inactive status filter.
 */
export function instanceIsActive(programIds: string[]): boolean {
    return programsByIds(programIds).some((p) => p.status === "active");
}

// ── Create-a-policy: the issuer-constraint plumbing ──────────────────────

export interface PolicyTypeOption {
    typeId: string;
    label: string;
    category: PolicyCategory;
    detail: string;
    constraint: PolicyConstraint;
    permission: PolicyPermission;
}

/** Every policy type a client can preview/create in the create-policy picker. */
export const POLICY_TYPE_LIST: PolicyTypeOption[] = POLICY_TYPES.map((t) => ({
    typeId: t.typeId,
    label: t.label,
    category: t.category,
    detail: t.detail,
    constraint: t.constraint,
    permission: t.permission
}));

/** Human-readable summary of a type's issuer constraint. */
/** Formats a single constraint bound (min/max) for display. */
export function formatBound(c: PolicyConstraint, value: number): string {
    if (c.kind !== "range") return String(value);
    switch (c.unit) {
        case "tier":
            return `Tier ${value}`;
        case "days":
            return `${value} days`;
        case "%":
            return `${value}%`;
        case "multiplier":
            return `${value}x`;
        default:
            return `${value.toLocaleString()} ${c.unit}`;
    }
}

export function constraintHint(c: PolicyConstraint): string {
    if (c.kind === "options") return `Allowed: ${c.options.join(", ")}`;
    if (!Number.isFinite(c.max)) {
        return `Min ${c.min.toLocaleString()} ${c.unit}`;
    }
    switch (c.unit) {
        case "multiplier":
            return `Allowed: ${c.min}x–${c.max}x`;
        case "tier":
            return `Allowed: Tier ${c.min}–${c.max}`;
        case "days":
            return `Allowed: ${c.min}–${c.max} days`;
        case "%":
            return `Allowed: ${c.min}–${c.max}%`;
        default:
            return `Allowed: ${c.min.toLocaleString()}–${c.max.toLocaleString()} ${c.unit}`;
    }
}

/**
 * Validate a raw input against the issuer constraint — the client can't
 * create a value outside the issuer's bound. Returns an error message, or
 * null when valid.
 */
export function validatePolicyValue(c: PolicyConstraint, raw: string): string | null {
    if (raw.trim() === "") return "Enter a value";
    if (c.kind === "options") {
        return c.options.includes(raw) ? null : "Choose an allowed value";
    }
    const n = Number(raw);
    if (Number.isNaN(n)) return "Enter a number";
    if (n < c.min || n > c.max) {
        return c.unit === "multiplier"
            ? `Must be between ${c.min}x and ${c.max}x`
            : `Must be between ${c.min.toLocaleString()} and ${c.max.toLocaleString()} ${c.unit}`;
    }
    return null;
}

/** Format a valid raw input into the display value stored on an instance. */
export function formatPolicyValue(c: PolicyConstraint, raw: string): string {
    if (c.kind === "options") return raw;
    const n = Number(raw);
    switch (c.unit) {
        case "multiplier":
            return `Up to ${n}x multiplier`;
        case "tier":
            return `Tier ${n}`;
        case "days":
            return `${n} days`;
        case "%":
            return `${n}%`;
        default:
            return `${n.toLocaleString()} ${c.unit}`;
    }
}

/** Build a new policy instance from a validated create-form submission. */
export function buildPolicyInstance(
    seq: number,
    typeId: string,
    value: string,
    programIds: string[]
): PolicyDefinition {
    const type = POLICY_TYPES.find((t) => t.typeId === typeId);
    return {
        id: `created-${seq}-${typeId}`,
        typeId,
        type: type?.label ?? typeId,
        category: type?.category ?? "eligibility",
        value,
        detail: type?.detail ?? "",
        programIds,
        constraint: type?.constraint ?? FALLBACK_CONSTRAINT,
        permission: type?.permission ?? "constrained"
    };
}

// ── Edit-in-place helpers ────────────────────────────────────────────────

/** Extract the editable raw value from a formatted display value, so the
 *  in-line editor opens pre-filled (e.g. "1,000" → "1000"). */
export function rawValueFor(c: PolicyConstraint, value: string): string {
    if (c.kind === "options") {
        return c.options.find((o) => value.includes(o)) ?? "";
    }
    const match = /-?\d+(?:\.\d+)?/.exec(value.replace(/,/g, ""));
    return match ? match[0] : "";
}

/**
 * Validate an edited value, honoring permission. `delegated` skips the
 * issuer bound (client decides), `constrained` enforces it, `locked`
 * is never editable so it always reports an error.
 */
export function validateEditedValue(permission: PolicyPermission, c: PolicyConstraint, raw: string): string | null {
    if (permission === "locked") return "Locked by distributor";
    if (raw.trim() === "") return "Enter a value";
    if (permission === "delegated") {
        if (c.kind === "range") {
            const n = Number(raw);
            if (Number.isNaN(n)) return "Enter a number";
            // Basic sanitization — no negative amounts/multipliers.
            if (n < 0) return "Enter a positive value";
        }
        return null;
    }
    return validatePolicyValue(c, raw);
}

/**
 * Build the typed save payload from a raw value.
 */
export function buildPolicyParameters(typeId: string, c: PolicyConstraint, raw: string): Record<string, unknown> {
    if (c.kind === "options") return { value: raw };
    const n = Number(raw);
    if (!Number.isFinite(n)) return { value: raw };
    switch (typeId) {
        case "min-balance":
            return { minBalance: n };
        case "min-holding-period":
            return { days: n };
        case "min-distribution-threshold":
            return { minAmount: n };
        default:
            break;
    }
    switch (c.unit) {
        case "multiplier":
            return { multiplier: n };
        case "tier":
            return { minTier: n };
        case "days":
            return { days: n };
        case "%":
            return { retentionPct: n };
        default:
            return { amount: n };
    }
}
